/**
 * VLM Platform — English. The voice (brand/BRAND.md §5): calm and specific — who, when, how much —
 * state first, detail second; one word per thing; separators are "•", never "·". Money, dates and
 * numbers are not written here: @vlm/i18n's formatters print them.
 */
export const en = {
  app: {
    name: 'VLM Platform',
    skip: 'Skip to content',
    menu: 'Menu',
    navigation: 'Main navigation',
  },

  common: {
    save: 'Save',
    saveChanges: 'Save changes',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    edit: 'Edit',
    delete: 'Delete',
    back: 'Back',
    add: 'Add',
    search: 'Search',
    clearSearch: 'Clear search',
    retry: 'Try again',
    loading: 'Loading',
    dismiss: 'Dismiss',
    actions: 'Actions',
    status: 'Status',
    all: 'All',
    notes: 'Notes',
    notesPlaceholder: 'Anything worth knowing',
    optional: 'optional',
    required: 'Required',
    tooLong: 'At most {max} characters',
    tooShort: 'At least {min} characters',
    invalidEmail: 'Enter an email address like name@company.com',
    invalidNumber: 'Enter a number',
    invalidDate: 'Enter a date',
    notNegative: 'Must be 0 or more',
    range: '{from}–{to} of {total}',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    rowsPerPage: 'Rows per page',
    noMatchTitle: 'Nothing matches',
    noMatchBody: 'Try another search, or clear the filters.',
    clearFilters: 'Clear filters',
    loadFailedTitle: "Couldn't load this",
    loadFailedBody: 'Check your connection and try again.',
    activate: 'Activate',
    deactivate: 'Deactivate',
    active: 'Active',
    inactive: 'Inactive',
    never: 'Never',
    none: 'None',
    view: 'View',
    download: 'Download',
    english: 'English',
    arabic: 'العربية',
    // the two halves of a bilingual field (components/common.tsx L10nInputs)
    inEnglish: 'English',
    inArabic: 'Arabic',
    yes: 'Yes',
    no: 'No',
    unlimited: 'Unlimited',
    saved: 'Saved',
    language: 'Language',
    open: 'Open',
  },

  nav: {
    dashboard: 'Overview',
    companies: 'Companies',
    invoices: 'Invoices',
    plans: 'Plans',
    billing: 'Billing settings',
    email: 'Email',
    users: 'Platform users',
    profile: 'Your profile',
  },

  shell: {
    theme: 'Theme',
    themeDay: 'Day',
    themeNight: 'Night',
    themeAuto: 'Match the system',
    switchLanguage: 'العربية',
    switchLanguageLabel: 'Switch to Arabic',
    signOut: 'Sign out',
    account: 'Account',
    openAdmin: 'Open the admin',
    openWebsite: 'Open the website',
    expandMenu: 'Expand menu',
    collapseMenu: 'Collapse menu',
  },

  auth: {
    title: 'Sign in',
    subtitle: 'VLM Platform • Viganium staff only',
    username: 'Username',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    submit: 'Sign in',
    usernameRequired: 'Enter your username',
    passwordRequired: 'Enter your password',
    expired: 'Your session ended. Sign in again.',
    checking: 'Signing you in',
    unreachableTitle: "Can't reach VLM",
    unreachableBody: 'The API is not answering. Check your connection; your sign-in is kept.',
    // the words on the sign-in picture's map (RouteScene, shown in capitals): a trip from a depot to the pin
    scene: {
      lat: '21°25′N',
      lng: '39°49′E',
      scale: 'Zoom 14 • 1:34,000',
      from: 'From Depot 3 • 08:02',
      eta: 'ETA 10:15',
    },
  },

  roles: {
    ADMIN: 'Admin',
    STAFF: 'Staff',
  },

  roleHints: {
    ADMIN: 'Everything, including platform users and billing settings.',
    STAFF: 'Companies, their users, plans, invoices and the website.',
  },

  companyRoles: {
    ADMIN: 'Admin',
    OPERATOR: 'Operator',
    VIEWER: 'Viewer',
  },

  companyRoleHints: {
    ADMIN: 'Everything in the company, including its users, alert settings and billing.',
    OPERATOR: 'Vehicles, tracking devices, assignments, geofences and alerts.',
    VIEWER: 'Sees the live map, trips, alerts and reports.',
  },

  companyStatus: {
    PENDING: 'Awaiting approval',
    ACTIVE: 'Active',
    SUSPENDED: 'Suspended',
    REJECTED: 'Rejected',
  },

  source: {
    LEGACY: 'From before the SaaS',
    REGISTRATION: 'Registered on the website',
    PLATFORM: 'Created by Viganium',
  },

  subscriptionStatus: {
    NONE: 'No plan',
    PENDING: 'Awaiting payment',
    ACTIVE: 'Paid',
    PAST_DUE: 'Overdue',
    CANCELED: 'Canceled',
  },

  invoiceStatus: {
    UNPAID: 'Unpaid',
    PAID: 'Paid',
    VOID: 'Void',
    OVERDUE: 'Overdue',
  },

  cycles: {
    MONTHLY: 'Monthly',
    YEARLY: 'Yearly',
  },

  perCycle: {
    MONTHLY: '{amount} a month',
    YEARLY: '{amount} a year',
  },

  methods: {
    BANK_TRANSFER: 'Bank transfer',
    CASH: 'Cash',
    CHEQUE: 'Cheque',
    OTHER: 'Other',
  },

  maps: {
    STANDARD: 'Standard map',
    GOOGLE: 'Google Maps',
    noKey: 'Google Maps is not set up on the server yet (GOOGLE_MAPS_API_KEY), so these users still see the standard map.',
  },

  emailStatus: {
    QUEUED: 'Queued',
    SENT: 'Sent',
    FAILED: 'Failed',
    SKIPPED: 'Skipped',
  },

  /** What wrote the email: the API's event "invoice.dueSoon" is emailEvents.invoice.dueSoon. */
  emailEvents: {
    company: {
      registered: 'Registration received',
      registeredStaff: 'New registration to review',
      approved: 'Company approved',
      rejected: 'Company rejected',
      suspended: 'Company suspended',
      reactivated: 'Company reactivated',
      trialEnding: 'Free trial ending',
    },
    invoice: {
      issued: 'Invoice issued',
      paid: 'Payment receipt',
      dueSoon: 'Payment due soon',
      overdue: 'Payment overdue',
    },
    alert: {
      raised: 'Vehicle alert',
    },
    email: {
      test: 'Test email',
    },
  },

  dashboard: {
    title: 'Overview',
    description: 'Who is waiting, who pays next and what is owed.',
    pending: 'Awaiting approval',
    active: 'Active companies',
    suspended: 'Suspended',
    mrr: 'Monthly recurring',
    mrrHint: 'Paid subscriptions, a yearly price spread over twelve months',
    paidThisMonth: 'Received this month',
    outstanding: 'Unpaid invoices',
    overdue: 'of it overdue: {amount}',
    noMoney: 'No subscription has been paid yet.',
    waitingTitle: 'Waiting for approval',
    waitingEmpty: 'Nobody is waiting. New registrations from the website land here.',
    renewalsTitle: 'Renewals in the next 14 days',
    renewalsEmpty: 'No paid period ends in the next two weeks.',
    trialsTitle: 'Free trials',
    trialsEmpty: 'No company is on a free trial.',
    trialEnds: 'Ends {date}',
    trialEnded: 'Last day {date}',
    overdueTitle: 'Overdue invoices',
    overdueEmpty: 'Nothing is overdue.',
    fleetTitle: 'On VLM today',
    vehicles: 'Active vehicles',
    devices: 'Tracking devices',
    users: 'Active users',
    review: 'Review',
    asked: 'Asked for {plan}',
    fleetOf: { one: '{count} vehicle', other: '{count} vehicles' },
  },

  companies: {
    title: 'Companies',
    description: 'Every customer of VLM: approve registrations, assign plans, record payments.',
    add: 'New company',
    search: 'Search name, code, email, phone',
    empty: 'No companies yet',
    emptyBody: 'Registrations from the website appear here, or create a company yourself.',
    company: 'Company',
    code: 'Code',
    contact: 'Contact',
    plan: 'Plan',
    usage: 'Vehicles • users',
    created: 'Created',
    tabAll: 'All',
    fields: {
      name: 'Company name',
      contactName: 'Contact person',
      email: 'Email',
      phone: 'Phone',
      city: 'City',
      country: 'Country',
      address: 'Address',
      taxNumber: 'Tax number',
      commercialRegistration: 'Commercial registration',
      language: 'Invoice language',
      fleetSize: 'Fleet size',
      notes: 'Notes (Viganium only)',
      map: 'Map',
    },
    mapFollowPlan: 'As its plan says ({map})',
    mapFollowNoPlan: 'As its plan says (the standard map until it has one)',
    mapFollowTrial: 'As its plan says (Google Maps during the free trial)',
    mapHint: 'Choose a map here only to give this company something other than its plan’s.',
    firstAdmin: 'Its first admin',
    firstAdminHint: 'They sign in to the admin with this username and password.',
    withAdmin: 'Create its first admin now',
    startPlan: 'Put it on a plan now',
    createdToast: '{name} created • {code}',
    savedToast: 'Company details saved',
    editTitle: 'Edit company details',
  },

  company: {
    overview: 'Overview',
    billing: 'Plan & invoices',
    users: 'Users',
    approve: 'Approve',
    reject: 'Reject',
    suspend: 'Suspend',
    reactivate: 'Reactivate',
    delete: 'Delete company',
    approveTitle: 'Approve {name}',
    approveBody: 'The company becomes active and its users can sign in to the admin. Start it on a free trial, a plan, or both — a plan’s first period is paid by bank transfer.',
    approveWithoutPlan: 'Approve without a plan (no limits, nothing to pay)',
    approveTrialNoPlan: 'No plan yet — assign one before the trial ends',
    approved: '{name} is approved',
    rejectTitle: 'Reject {name}',
    rejectBody: 'Its users will never be able to sign in. You can approve it later if this was a mistake.',
    rejected: '{name} is rejected',
    suspendTitle: 'Suspend {name}',
    suspendBody: 'Its users are signed out at once and cannot sign in until you reactivate it. Its devices keep reporting; nothing is deleted.',
    suspended: '{name} is suspended',
    reactivated: '{name} is active again',
    deleteTitle: 'Delete {name}?',
    deleteBody: 'Only a pending or rejected company with no vehicles, devices, geofences or invoices can be deleted. Its users are deleted with it. This cannot be undone.',
    deleted: '{name} is deleted',
    reason: 'Reason',
    reasonPlaceholder: 'The company sees this when it is told why',
    statusSince: 'Reason: {reason}',
    details: 'Details',
    source: 'Source',
    registered: 'Registered',
    approvedAt: 'Approved',
    requested: 'Asked for',
    requestedNone: 'No plan chosen',
    usageTitle: 'Usage',
    vehicles: 'Active vehicles',
    devices: 'Tracking devices',
    activeUsers: 'Active users',
    ofLimit: '{used} of {limit}',
    notFound: 'This company does not exist',
    backToList: 'All companies',
    adminLink: 'Its users sign in at the admin',
    emails: 'Emails',
    mapFromPlan: '{map} • from its plan',
    mapSetHere: '{map} • set for this company',
    mapFromTrial: '{map} • during the free trial',
    noLimitTrial: '{used} • no limit during the trial',
  },

  subscription: {
    title: 'Plan',
    none: 'No plan',
    noneBody: 'Without a plan the company has no limits and nothing to pay.',
    noneBodyTrial: 'Once its free trial is over, its users can sign in only with a plan.',
    assign: 'Assign a plan',
    change: 'Change plan',
    cancel: 'Cancel subscription',
    plan: 'Plan',
    cycle: 'Billing cycle',
    price: 'Price per cycle, before VAT',
    priceHint: 'Leave empty for the plan’s price ({price}).',
    priceHintNone: 'This plan has no price for that cycle — enter one.',
    paidThrough: 'Paid through',
    notPaidYet: 'Not paid yet',
    nextPayment: 'Next payment',
    nextPaymentInvoice: '{number} • due {date}',
    assignedToast: 'The plan is saved',
    changeHint: 'A new plan applies from the next invoice; its limits apply at once.',
    cancelTitle: 'Cancel the subscription?',
    cancelBody: 'The company keeps its access (suspend it to cut that off) and nothing more is invoiced. Unpaid invoices stay until you void them.',
    canceledToast: 'The subscription is canceled',
    started: 'Since',
    afterTrial: 'The trial runs until {end}: this plan’s limits apply after it, and its first period starts on {start}.',
  },

  /** A free trial (docs/SAAS.md §2): everything unlimited for the days Viganium chooses, before a plan. */
  trial: {
    title: 'Free trial',
    start: 'Start with a free trial',
    give: 'Give a free trial',
    giveMore: 'Give more days',
    change: 'Change the trial',
    end: 'End the trial',
    days: 'Days',
    daysHint: 'From 1 to 365 days, today included.',
    daysFromStart: 'Counted from its first day, {date}, which is included.',
    tooShort: 'To run through today it needs at least {min} days.',
    endsOn: 'Ends on {date}',
    rules: 'Nothing is limited during the trial: any number of vehicles and users, and Google Maps unless this company’s map is set by hand. When it ends, its plan applies — and without a plan its users cannot sign in until you assign one or give more days.',
    started: 'Started',
    lastDay: 'Last day',
    left: 'Days left',
    lengthLabel: 'Length',
    length: { one: '{count} day', other: '{count} days' },
    daysLeft: { one: 'Free trial • last day', other: 'Free trial • {count} days left' },
    endedNoPlan: 'Trial ended • no plan',
    none: 'No free trial',
    noneBody: 'Give one to lift every limit for a number of days — say, while the company tries VLM before choosing a plan.',
    runningBody: 'Everything is unlimited until the end of {date}. Then its plan applies.',
    runningNoPlan: 'Everything is unlimited until the end of {date}. Assign a plan before then: once the trial is over, its users cannot sign in without one.',
    endedLocked: 'The trial is over — its last day was {date}. Its users cannot sign in until you assign a plan or give more days.',
    endedWithPlan: 'The trial is over — its last day was {date}; its plan applies.',
    endTitle: 'End the trial now?',
    endBodyPlan: 'Its plan applies from now on: its limits, its map and its first payment.',
    endBodyNoPlan: 'It has no plan, so its users are signed out and cannot sign in until you assign one or give more days.',
    endedToast: 'The trial has ended',
    savedToast: 'The trial runs until {date}',
  },

  invoices: {
    title: 'Invoices',
    description: 'Every invoice of every company. A transfer is recorded on its invoice.',
    search: 'Search number or company',
    empty: 'No invoices yet',
    emptyBody: 'Issue one from a company’s Plan & invoices tab.',
    companyEmpty: 'No invoices yet',
    companyEmptyBody: 'Issue the first one, or record a transfer that already arrived.',
    number: 'Invoice',
    company: 'Company',
    period: 'Period',
    issueDate: 'Issued',
    dueDate: 'Due',
    total: 'Total',
    paidOn: 'Paid on',
    issue: 'Issue an invoice',
    recordPayment: 'Record a transfer',
    markPaid: 'Record payment',
    void: 'Void',
    downloadEn: 'PDF in English',
    downloadAr: 'PDF in Arabic',
    issueTitle: 'Issue an invoice',
    issueBody: 'For the next period of {plan}. The company sees it on its billing page and pays it by bank transfer.',
    payTitle: 'Record the transfer for {number}',
    payBody: 'The subscription becomes active through the invoice’s period.',
    recordTitle: 'Record a transfer',
    recordBody: 'Issues the invoice for the next period of {plan} and marks it paid — the subscription becomes active through that period.',
    periodStart: 'Period from',
    periodEnd: 'Period to',
    subtotal: 'Amount before VAT',
    subtotalHint: 'Leave as it is for the subscription’s price.',
    vat: 'VAT {rate}',
    totalLine: 'Total with VAT',
    method: 'How it was paid',
    reference: 'Transfer reference',
    referenceHint: 'The bank’s reference, so it can be found again.',
    note: 'Note on the payment',
    invoiceNote: 'Note printed on the invoice',
    voidTitle: 'Void {number}',
    voidBody: 'A void invoice is kept, numbered, but never paid. A paid invoice cannot be voided.',
    voidReason: 'Why',
    issuedToast: '{number} issued',
    paidToast: '{number} is paid',
    voidedToast: '{number} is void',
    filterAny: 'Any status',
    overdueBadge: 'Overdue',
    noPlanFirst: 'Assign a plan before invoicing.',
    notFound: 'This invoice does not exist',
    invoicePart: 'The invoice',
    paymentPart: 'The payment',
  },

  plans: {
    title: 'Plans',
    description: 'What companies subscribe to. Public, active plans are on the website.',
    add: 'New plan',
    edit: 'Edit plan',
    empty: 'No plans yet',
    emptyBody: 'Create the plans companies choose from on the website.',
    code: 'Code',
    codeHint: 'Lower-case letters, digits and dashes — it is in the website’s links.',
    nameEn: 'Name in English',
    nameAr: 'Name in Arabic',
    descriptionEn: 'Description in English',
    descriptionAr: 'Description in Arabic',
    featuresEn: 'Features in English',
    featuresAr: 'Features in Arabic',
    featuresHint: 'One per line, as the website lists them.',
    currency: 'Currency',
    monthlyPrice: 'Monthly price',
    yearlyPrice: 'Yearly price',
    priceHint: 'Before VAT. Empty = not sold that way.',
    maxVehicles: 'Active vehicles',
    maxUsers: 'Active users',
    limitHint: 'Empty = unlimited.',
    map: 'Map',
    mapHint: 'What the users of its companies see. Google bills Viganium for every map they open.',
    public: 'On the website',
    active: 'Offered',
    activeHint: 'Off: no new company can be put on it; those on it keep it.',
    featured: 'Highlighted on the website',
    sortOrder: 'Order',
    subscribers: { zero: 'No companies', one: '{count} company', other: '{count} companies' },
    prices: 'Prices',
    limits: 'Limits',
    hidden: 'Hidden',
    retired: 'Not offered',
    createdToast: '{name} created',
    savedToast: '{name} saved',
    deleteTitle: 'Delete {name}?',
    deleteBody: 'Only a plan no company has ever been on can be deleted. Otherwise turn “Offered” off.',
    deletedToast: '{name} deleted',
    noPrice: 'Not sold',
    notFound: 'This plan does not exist',
  },

  billing: {
    title: 'Billing settings',
    description: 'Viganium as the seller on every invoice, VAT, payment terms and the account companies transfer to.',
    adminOnly: 'Only a platform admin can change these.',
    seller: 'The seller',
    sellerName: 'Seller name',
    sellerAddress: 'Seller address',
    taxNumber: 'VAT number',
    commercialRegistration: 'Commercial registration',
    email: 'Billing email',
    phone: 'Billing phone',
    terms: 'Tax and terms',
    vatRate: 'VAT rate (%)',
    currency: 'Default currency for new plans',
    paymentTermsDays: 'Days to pay',
    paymentTermsHint: 'Due date = issue date + this, never before the period starts.',
    bank: 'Where companies transfer',
    bankName: 'Bank',
    accountName: 'Account name',
    iban: 'IBAN',
    swift: 'SWIFT / BIC',
    invoiceNotes: 'Invoice footer',
    savedToast: 'Billing settings saved',
    snapshotNote: 'Issued invoices keep the seller details they were issued with; unpaid ones always show the current bank account.',
  },

  email: {
    title: 'Email',
    description: 'Every email VLM sends to companies and to Viganium, through ZeptoMail, and what became of it.',
    sending: 'Sending',
    sendingHint: 'Through ZeptoMail',
    notConfigured: 'Email is not set up on the API yet',
    missing: 'Missing settings:',
    notConfiguredBody: 'Set them in the API’s environment and restart it. Until then no email leaves VLM: each one is logged as skipped.',
    send: 'Send emails',
    onHint: 'On: every new email goes to ZeptoMail; one it cannot take right away is tried again.',
    onNotConfigured: 'On, but nothing can leave until email is set up on the API.',
    offHint: 'Off: new emails are logged as skipped and nothing is sent. A skipped email can be resent.',
    adminOnly: 'Only a platform admin can switch this.',
    offTitle: 'Stop sending emails?',
    offBody: 'New emails — invoices and reminders included — are logged as skipped and not sent unless someone resends them.',
    turnOff: 'Stop sending',
    onToast: 'Emails are on',
    offToast: 'Emails are off: new ones are logged as skipped',
    from: 'From',
    replyTo: 'Replies go to',
    replyToNone: 'Set a billing email',
    endpoint: 'Sent through',
    notSet: 'Not set',
    lastSevenDays: 'Last 7 days',
    recipients: 'Who receives what',
    recipientsBilling: 'Invoices, receipts and payment reminders: the company’s email (its billing email) or, while it has none, its active admins.',
    recipientsAccount: 'Approval, rejection, suspension and reactivation: the company’s active admins.',
    recipientsRegistration: 'A registration: the person who registered, and every active platform user.',
    recipientsAlerts: 'Vehicle alerts: the addresses each company’s admins set for an alert type in VLM Admin › Alert settings.',
    test: 'Send me a test email',
    testSent: 'Test email sent to {address}',
    testQueued: 'The test email to {address} is queued; VLM tries again shortly.',
    messages: 'Emails',
    search: 'Search address, name, subject or company',
    when: 'When',
    to: 'To',
    subject: 'Subject',
    company: 'Company',
    resend: 'Resend',
    queuedFor: 'Queued again for {address}',
    sentTo: 'Sent to {address}',
    notSentTo: 'The email to {address} was not sent.',
    attemptCount: { one: '{count} attempt', other: '{count} attempts' },
    empty: 'No emails yet',
    emptyBody: 'Registrations, approvals, invoices and reminders appear here as VLM sends them.',
    companyEmpty: 'No emails to {name} yet',
    companyEmptyBody: 'Its account and billing emails appear here.',
    thisCompany: 'This company',
    removeCompany: 'Show every company’s emails',
    event: 'Event',
    attempts: 'Attempts',
    created: 'Created',
    sent: 'Sent',
    nextAttempt: 'Next attempt',
    attachments: 'Attachments',
    preview: 'Preview',
    previewAs: 'Show the email',
    html: 'Formatted',
    text: 'Plain text',
    previewTitle: 'The email “{subject}”',
    noHtml: 'This email was sent as plain text only.',
    noText: 'This email has no plain-text version.',
    lastError: {
      QUEUED: 'Why the last attempt failed',
      SENT: 'ZeptoMail’s answer',
      FAILED: 'Why it failed',
      SKIPPED: 'Why it was skipped',
    },
  },

  users: {
    title: 'Platform users',
    description: 'Viganium’s staff who sign in here. Companies’ users are managed on each company.',
    add: 'Add a platform user',
    edit: 'Edit platform user',
    username: 'Username',
    usernameHint: '3–50 letters, digits, dots, dashes or underscores.',
    usernameLocked: 'A username cannot be changed.',
    fullName: 'Full name',
    email: 'Email',
    role: 'Role',
    password: 'Password',
    passwordHint: 'At least 8 characters.',
    lastLogin: 'Last sign-in',
    you: 'You',
    createdToast: '{name} added',
    savedToast: '{name} saved',
    activated: '{name} can sign in again',
    deactivated: '{name} can no longer sign in',
    activateTitle: 'Let {name} sign in again?',
    activateBody: 'They sign in with their current password.',
    deactivateTitle: 'Stop {name} signing in?',
    deactivateBody: 'They are signed out everywhere at once.',
    setPassword: 'Set a new password',
    setPasswordTitle: 'New password for {name}',
    setPasswordBody: 'They are signed out everywhere and sign in with this one.',
    passwordSet: 'The new password is set',
    notFound: 'This user does not exist',
  },

  companyUsers: {
    title: 'Users of {name}',
    description: 'The same roles as in the admin. The plan’s user limit does not bind Viganium.',
    add: 'Add a user',
    edit: 'Edit user',
    empty: 'No users',
    emptyBody: 'Add the company’s first admin so it can sign in.',
    passwordSetBody: 'Their open sessions run until they expire; they sign in with this one from now on.',
  },

  profile: {
    title: 'Your profile',
    description: 'Your details and your password.',
    details: 'Details',
    password: 'Password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'New password again',
    mismatch: 'The two passwords differ',
    changePassword: 'Change password',
    changed: 'Password changed — every other session is signed out',
    saved: 'Your details are saved',
  },

  errors: {
    invalidCredentials: 'That username and password do not match.',
    accountDisabled: 'This account is deactivated.',
    rateLimited: 'Too many attempts. Wait a few minutes and try again.',
    usernameTaken: 'This username is taken.',
    planCodeTaken: 'Another plan has this code.',
    planInUse: 'Companies have been on this plan, so it cannot be deleted. Turn “Offered” off instead.',
    lastAdmin: 'There must always be one active admin.',
    periodInvoiced: 'An invoice already covers part of this period.',
    priceRequired: 'This plan has no price for that cycle — enter one.',
    notFound: 'This no longer exists. Refresh the page.',
    accessDenied: 'Your role cannot do this.',
    network: 'Can’t reach VLM. Check your connection.',
    server: 'VLM ran into a problem ({status}). Try again.',
    unknown: 'Something went wrong. Try again.',
    validation: 'Check the highlighted fields.',
  },

  pages: {
    forbiddenTitle: 'Not for your role',
    forbiddenBody: 'A platform admin can open this page.',
    notFoundTitle: 'Nothing here',
    notFoundBody: 'The page you asked for does not exist.',
    goHome: 'Go to the overview',
    // a form page whose step was taken meanwhile (approved, paid, voided…)
    staleTitle: 'Nothing to do here any more',
    staleBody: '{name} is now: {status}.',
  },
} as const;

export type PlatformMessages = typeof en;
